import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { ChartColumnIcon, PlusIcon, SettingsIcon } from 'lucide-react'
import {
  Button,
  DefaultTooltip,
  MenuItem,
  MenuSubitems,
  cn,
  useSidebarContext,
} from 'twyne-new.ui'

import { AppSidebarSubitem } from '@/components/app-layout/SidebarSubitems'
import { useAppLayoutContext } from '@/components/app-layout/context'
import history from 'utils/history'

import { useCustomReportStore } from '../store'
import { isReportPinned } from '../utils/reportViewConfig'

import CreateReportViewPopover, { ADD_BUTTON_ID } from './CreateReportViewPopover'
import ReportPinDropdown, { PIN_BUTTON_ID } from './ReportPinDropdown'

function isCustomReportPath(pathname, viewId) {
  if (!viewId) return pathname === '/reports/custom'
  return pathname === `/reports/custom/${viewId}`
}

export default function CustomReportSidebarSection({ hasPermission }) {
  const id = useId()
  const { pathname } = useLocation()
  const { expanded, setExpanded, collapsed } = useSidebarContext()
  const { setMenuCollapsed } = useAppLayoutContext()
  const matchedOnceRef = useRef(false)
  const [rowHovered, setRowHovered] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [pinOpen, setPinOpen] = useState(false)

  const {
    reportView: { useLoad },
    views: { items },
  } = useCustomReportStore()

  useLoad()

  const pinnedItems = useMemo(
    () => items.filter(item => isReportPinned(item.configuration)),
    [items],
  )

  const isOnCustomRoute = pathname === '/reports/custom' || pathname.startsWith('/reports/custom/')
  const hasActiveChild = pinnedItems.some(item => isCustomReportPath(pathname, item.id))
  const active = expanded === id || isOnCustomRoute || hasActiveChild
  const allowed = !hasPermission || hasPermission('path')

  useEffect(() => {
    if (!allowed || !isOnCustomRoute) return
    setMenuCollapsed(false)
  }, [allowed, isOnCustomRoute, setMenuCollapsed])

  useEffect(() => {
    if (!allowed) return
    if (isOnCustomRoute && !collapsed && !matchedOnceRef.current) {
      setExpanded(id)
    }
    matchedOnceRef.current = isOnCustomRoute
  }, [allowed, isOnCustomRoute, id, setExpanded, collapsed])

  const handleMenuItemClick = useCallback(event => {
    if (event.target.closest('[data-custom-report-action]')) return
    setExpanded(id)
    if (pathname !== '/reports/custom') {
      history.push('/reports/custom')
    }
  }, [id, pathname, setExpanded])

  const handleAddClick = useCallback(event => {
    event.preventDefault()
    event.stopPropagation()
    setCreateOpen(true)
  }, [])

  const handlePinClick = useCallback(event => {
    event.preventDefault()
    event.stopPropagation()
    setPinOpen(true)
  }, [])

  const actionsVisible = createOpen || pinOpen || rowHovered
  const actionClassName = cn(
    'h-6 w-6 p-0 transition-opacity',
    actionsVisible ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto',
  )

  if (!allowed) return null

  return <>
    <DefaultTooltip content={!expanded && 'Custom'} wrapperProps={{ asChild: true }} side="right">
      <MenuItem
        icon={ChartColumnIcon}
        active={active}
        className="group"
        onMouseEnter={() => setRowHovered(true)}
        onMouseLeave={() => setRowHovered(false)}
        onClick={handleMenuItemClick}
      >
        <span className="group flex w-full min-w-0 items-center justify-between gap-2">
          <span className="truncate">Custom</span>
          <span className="inline-flex shrink-0 items-center" data-custom-report-action>
            <DefaultTooltip content="Add report" wrapperProps={{ asChild: true }} side="top">
              <span id={ADD_BUTTON_ID} className="inline-flex shrink-0 items-center">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  aria-label="Add report"
                  className={actionClassName}
                  onClick={handleAddClick}
                >
                  <PlusIcon size={14} />
                </Button>
              </span>
            </DefaultTooltip>
            <DefaultTooltip content="Pinned reports" wrapperProps={{ asChild: true }} side="top">
              <span id={PIN_BUTTON_ID} className="inline-flex shrink-0 items-center">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  aria-label="Pinned reports"
                  className={actionClassName}
                  onClick={handlePinClick}
                >
                  <SettingsIcon size={14} />
                </Button>
              </span>
            </DefaultTooltip>
          </span>
        </span>
      </MenuItem>
    </DefaultTooltip>
    <MenuSubitems collapsed={!active}>
      {pinnedItems.map(item => <AppSidebarSubitem
        key={item.id}
        to={`/reports/custom/${item.id}`}
        alias={item.description}
      />)}
      {!pinnedItems.length && <p className="px-3 py-1.5 text-xs twy-text-base-muted">
        No pinned reports
      </p>}
    </MenuSubitems>
    <CreateReportViewPopover open={createOpen} onOpenChange={setCreateOpen} />
    <ReportPinDropdown open={pinOpen} onOpenChange={setPinOpen} />
  </>
}
